import intersectionWith from 'lodash/intersectionWith';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import I18n from 'common/i18n';
import { ForgeCheckbox } from '@tylertech/forge-react';
import {
  AccordionContainerV2 as AccordionContainer,
  AccordionPaneV2 as AccordionPane
} from 'common/components/Accordion';
import MetadataProvider from 'common/visualizations/dataProviders/MetadataProvider';
import { ViewColumn } from 'common/types/viewColumn';
import { Hierarchy, HierarchyColumnConfig, Vif } from 'common/visualizations/vif';

import { MAX_HIERARCHIES } from 'common/authoring_workflow/constants';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import {
  getCurrentVif,
  getTableColumns,
  getTableHierarchies
} from 'common/authoring_workflow/selectors/vifAuthoring';
import {
  duplicateTableHierarchy,
  removeTableHierarchy,
  setActiveHierarchyId,
  showGrandTotal,
  showSubTotal,
  showNetTotal,
  updateTableHierarchyName
} from 'common/authoring_workflow/actions';
import TableHierarchyGroupingHeader from './components/TableHierarchyGroupingHeader';
import NetTotalEditor from './components/NetTotalEditor';

import FeatureFlags from 'common/feature_flags';

const scope = 'shared.visualizations.panes.data.fields.table_hierarchies';

export interface TableHierarchyGroupingsProps {
  hierarchyIndex: number;
}

export const TableHierarchyGroupings = (props: TableHierarchyGroupingsProps) => {
  const { hierarchyIndex } = props;
  const [allColumnMetadata, setAllColumnMetadata] = useState<ViewColumn[]>([]);

  const dispatch = useDispatch();

  const vif: Vif = useSelector((state: AuthoringWorkflowState) => {
    return getCurrentVif(state.vifAuthoring);
  }, isEqual);

  const tableHierarchies: Hierarchy[] = useSelector(
    (state: AuthoringWorkflowState) => getTableHierarchies(state.vifAuthoring),
    isEqual
  );

  const hierarchy = tableHierarchies[hierarchyIndex] ?? {};

  const existingColumnConfigs = useSelector((state: AuthoringWorkflowState) => {
    const tableColumns = getTableColumns(state.vifAuthoring);
    return intersectionWith(
      tableHierarchies[hierarchyIndex]?.columnConfigurations ?? [],
      tableColumns,
      (h: HierarchyColumnConfig, c: ViewColumn) => c.fieldName === h.columnName
    );
  }, isEqual);

  const hierarchyGroupingColumnConfigs = existingColumnConfigs.filter(
    (columnConfig) => columnConfig.isGrouping
  );

  useEffect(() => {
    async function updateColumnMetadata() {
      const metadata = await MetadataProvider.fromVif(vif).getDatasetMetadata();

      setAllColumnMetadata(metadata.columns || []);
    }

    updateColumnMetadata();
  }, [vif]);

  const accordionPaneTitle = (
    <TableHierarchyGroupingHeader
      columnMetadata={allColumnMetadata}
      hierarchy={hierarchy}
      duplicationAllowed={tableHierarchies.length < MAX_HIERARCHIES}
      removeAllowed={tableHierarchies.length > 1}
      hierarchyGroupingColumnConfigs={hierarchyGroupingColumnConfigs}
      setActiveHierarchy={() => dispatch(setActiveHierarchyId(hierarchy.id))}
      handleOnDuplicateHierarchy={() => dispatch(duplicateTableHierarchy(hierarchyIndex))}
      handleOnRemoveHierarchy={() => dispatch(removeTableHierarchy(hierarchyIndex))}
      handleOnUpdateHierarchyName={(name) => dispatch(updateTableHierarchyName(hierarchyIndex, name))}
    />
  );

  const renderAbstractTotal = (
    cssLabel: string,
    controlBoolean: boolean,
    showTotalAction: (hierarchyIndex: number, shouldShow: boolean) => void,
    translationKey: string
  ) => {
    const checkboxAttributes = {
      id: `show-${cssLabel}-${hierarchyIndex}-checkbox`,
      checked: controlBoolean,
      onClick: () => dispatch(showTotalAction(hierarchyIndex, !controlBoolean)),
      'data-testid': `table-show-${cssLabel}-checkbox`,
      readOnly: true
    };

    return (
      <div className={`${cssLabel}s-container abstract-totals-container`}>
        <ForgeCheckbox {...checkboxAttributes}>{I18n.t(translationKey, { scope })}</ForgeCheckbox>
      </div>
    );
  };

  const renderGrandTotals = () =>
    renderAbstractTotal('grand-total', hierarchy.showGrandTotal, showGrandTotal, 'show_totals');

  const renderSubTotals = () =>
    renderAbstractTotal('subtotal', hierarchy.showSubTotal, showSubTotal, 'show_subtotals');

  const renderNetTotals = () =>
    renderAbstractTotal('net-total', hierarchy.showNetTotal, showNetTotal, 'show_net_totals');

  return (
    <AccordionContainer>
      <AccordionPane
        title={accordionPaneTitle}
        toggleOnCaretOnly
        onOpen={() => {
          dispatch(setActiveHierarchyId(hierarchy.id));
        }}
      >
        <div className="table-hierarchies-list" data-testid="table-hierarchies-list">
          <div className="totals-container">
            {renderGrandTotals()}
            {renderSubTotals()}
            {FeatureFlags.value('enable_prototype_net_total') && renderNetTotals()}
          </div>
          {FeatureFlags.value('enable_prototype_net_total') && hierarchy.showNetTotal && (
            <NetTotalEditor hierarchyIndex={hierarchyIndex} />
          )}
        </div>
      </AccordionPane>
    </AccordionContainer>
  );
};
